"use client";

import { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  DollarSign,
  Loader2,
  Search,
  TrendingDown,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";

import { Badge } from "@/app/_module/components/ui/badge";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Input } from "@/app/_module/components/ui/input";

import type { AgencyUserWithAgency } from "../../../types/types";
import type {
  TechficientAgencyTotals,
  TechficientAgentRollupRow,
  TechficientCarrierBreakdownRow,
} from "@/app/_module/services/techficientService";

import { useTechficientAgencyDashboard } from "./useTechficientDashboard";

interface TechficientCommissionsTabProps {
  agencyId: string;
  onViewAgentDetails: (agencyUser: AgencyUserWithAgency) => void;
}

const fmtCurrency = (n: number | null | undefined) => {
  if (n == null) return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  const abs = Math.round(Math.abs(x));
  return `${x < 0 ? "-" : ""}$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const fmtPercent = (n: number | null | undefined) => {
  const x = Number(n);
  return n == null || !Number.isFinite(x) ? "—" : `${x.toFixed(1)}%`;
};

const fmtCount = (n: number | null | undefined) => {
  const x = Number(n);
  return n == null || !Number.isFinite(x) ? "0" : x.toLocaleString();
};

const fmtRelativeTime = (iso: string | null | undefined): string => {
  if (!iso) return "Never";
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return "Never";
  const diff = Date.now() - d;
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
};

/**
 * "Commissions" tab on the Agency Dashboard — Techficient/MRFG only.
 *
 * Surfaces commission lifecycle across the whole agency tree:
 *   - KPI hero: net in-pocket / advanced / earned / charged back / pending projected
 *   - Funnel:   quotes → cases → sold/approved → lapsed/declined
 *   - Agent table: per-agent provisioning state + production + commission
 *   - Carrier mix: case volume + sold rate per carrier
 *
 * All numbers come from one API call that walks the agency tree via recursive
 * CTE, so an agency owner sees their whole downline; a sub-agency owner sees
 * only their slice. The same lead-scoped composite key that prevents the
 * cross-agency leak on /quote-enroll is reused here.
 */
export function TechficientCommissionsTab({
  agencyId,
  onViewAgentDetails,
}: TechficientCommissionsTabProps) {
  const { data, isLoading, error } = useTechficientAgencyDashboard(agencyId);

  const [search, setSearch] = useState("");

  const filteredAgents = useMemo(() => {
    if (!data?.agents) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.agents;
    return data.agents.filter(
      (a) =>
        a.name?.toLowerCase().includes(q) ||
        a.email?.toLowerCase().includes(q) ||
        a.agencyName?.toLowerCase().includes(q),
    );
  }, [data?.agents, search]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="h-6 w-6 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center min-h-[200px]">
          <div className="text-center">
            <AlertTriangle className="h-6 w-6 text-amber-600 mx-auto mb-2" />
            <h3 className="text-base font-semibold text-gray-900">
              Couldn&apos;t load commissions
            </h3>
            <p className="text-sm text-gray-500 mt-1 max-w-md">
              Quote &amp; Enroll isn&apos;t enabled for this agency yet, or the
              service is temporarily unavailable.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const totals = data.agencyTotals;

  return (
    <div className="space-y-6">
      <CommissionsHero totals={totals} />
      <FunnelStrip totals={totals} />

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6">
        <AgentTable
          agents={filteredAgents}
          totalCount={data.agents?.length ?? 0}
          search={search}
          onSearchChange={setSearch}
          onViewAgentDetails={onViewAgentDetails}
        />
        <CarrierMixCard carriers={data.carriers} />
      </div>
    </div>
  );
}

// ───────────────────────── KPI hero ─────────────────────────

function CommissionsHero({ totals }: { totals: TechficientAgencyTotals }) {
  const cards = [
    {
      label: "Net In-Pocket",
      value: fmtCurrency(totals.commissionNetInPocket),
      helper: "Advanced + earned − chargebacks",
      icon: DollarSign,
      tone: "emerald",
    },
    {
      label: "Advanced",
      value: fmtCurrency(totals.commissionAdvanced),
      helper: "Awaiting earn window",
      icon: TrendingUp,
      tone: "blue",
    },
    {
      label: "Earned",
      value: fmtCurrency(totals.commissionEarned),
      helper: "Past chargeback window",
      icon: CheckCircle2,
      tone: "emerald",
    },
    {
      label: "Charged Back",
      value: fmtCurrency(totals.commissionChargedBack),
      helper: "Reclaimed by carrier",
      icon: TrendingDown,
      tone: "rose",
    },
    {
      label: "Pending Projected",
      value: fmtCurrency(totals.commissionPendingExpected),
      helper: "Cases in underwriting",
      icon: Zap,
      tone: "amber",
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
      {cards.map((c) => (
        <Card key={c.label} className="overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">
                  {c.label}
                </p>
                <p className="text-2xl font-semibold text-gray-900 mt-1 tabular-nums">
                  {c.value}
                </p>
                <p className="text-[11px] text-gray-400 mt-1">{c.helper}</p>
              </div>
              <span
                className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${
                  c.tone === "emerald"
                    ? "bg-emerald-50 text-emerald-600"
                    : c.tone === "blue"
                    ? "bg-blue-50 text-blue-600"
                    : c.tone === "rose"
                    ? "bg-rose-50 text-rose-600"
                    : "bg-amber-50 text-amber-600"
                }`}
              >
                <c.icon className="h-4 w-4" />
              </span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ───────────────────────── Funnel strip ─────────────────────────

function FunnelStrip({ totals }: { totals: TechficientAgencyTotals }) {
  const stages = [
    { label: "Agents Registered", value: totals.provisionedAgentCount, denom: totals.agentCount },
    { label: "Quotes Run", value: totals.quoteCount },
    { label: "Cases Opened", value: totals.totalCases },
    { label: "Sold / Approved", value: totals.soldApproved },
    { label: "Lapsed / Declined", value: totals.lapsedDeclined },
  ];

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Pipeline</h3>
            <p className="text-xs text-gray-500">
              Quote-to-app conversion:{" "}
              <span className="font-medium text-gray-700">
                {fmtPercent(totals.quoteToAppConversion)}
              </span>
              {" · "}
              Applied face:{" "}
              <span className="font-medium text-gray-700">
                {fmtCurrency(totals.appliedFaceTotal)}
              </span>
              {" · "}
              Approved face:{" "}
              <span className="font-medium text-gray-700">
                {fmtCurrency(totals.approvedFaceTotal)}
              </span>
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {stages.map((s) => (
            <div key={s.label} className="rounded-lg bg-gray-50 px-3 py-3">
              <p className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">
                {s.label}
              </p>
              <p className="text-xl font-semibold text-gray-900 tabular-nums mt-0.5">
                {fmtCount(s.value)}
                {"denom" in s && s.denom != null ? (
                  <span className="text-sm text-gray-400 font-normal">
                    {" "}
                    / {fmtCount(s.denom)}
                  </span>
                ) : null}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ───────────────────────── Agent table ─────────────────────────

function AgentTable({
  agents,
  totalCount,
  search,
  onSearchChange,
  onViewAgentDetails,
}: {
  agents: TechficientAgentRollupRow[];
  totalCount: number;
  search: string;
  onSearchChange: (v: string) => void;
  onViewAgentDetails: (agencyUser: AgencyUserWithAgency) => void;
}) {
  const isFiltered = search.trim().length > 0;
  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-900">
              Agents — Commission &amp; Production
            </h3>
            <Badge variant="secondary" className="ml-1">
              {isFiltered ? `${agents.length} of ${totalCount}` : totalCount}
            </Badge>
          </div>
          <div className="relative w-64">
            <Search className="h-3.5 w-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              placeholder="Search agents…"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>

        {/* Vertical scroll caps the table height so a large agency (100+
            agents) scrolls inside the card instead of growing the page
            unbounded. The header is sticky so column labels stay visible
            while scrolling the body. max-h chosen to show ~12 rows before
            scrolling kicks in. */}
        <div className="overflow-auto max-h-[36rem]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500 font-medium shadow-[0_1px_0_0_rgba(0,0,0,0.06)]">
              <tr>
                <th className="text-left px-4 py-2.5">Agent</th>
                <th className="text-left px-4 py-2.5">Agency</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Quotes</th>
                <th className="text-right px-4 py-2.5">Cases</th>
                <th className="text-right px-4 py-2.5">Sold</th>
                <th className="text-right px-4 py-2.5">Applied Face</th>
                <th className="text-right px-4 py-2.5">Advanced</th>
                <th className="text-right px-4 py-2.5">Charged Back</th>
                <th className="text-right px-4 py-2.5">Earned</th>
                <th className="text-right px-4 py-2.5">Net</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {agents.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    className="px-4 py-10 text-center text-sm text-gray-500"
                  >
                    No agents match your filter.
                  </td>
                </tr>
              ) : (
                agents.map((a) => {
                  const net =
                    Number(a.commissionAdvanced || 0) +
                    Number(a.commissionEarned || 0) -
                    Number(a.commissionChargedBack || 0);
                  return (
                    <tr
                      key={a.userId}
                      className="hover:bg-blue-50/30 cursor-pointer"
                      onClick={() =>
                        onViewAgentDetails({
                          userId: a.userId,
                          user: { id: a.userId, name: a.name, email: a.email },
                        } as unknown as AgencyUserWithAgency)
                      }
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {a.name || "—"}
                        </div>
                        <div className="text-xs text-gray-500">{a.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-700 truncate max-w-[160px]">
                          {a.agencyName || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {a.techficientProvisioned ? (
                          <div className="space-y-0.5">
                            <Badge
                              variant="secondary"
                              className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50"
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Registered
                            </Badge>
                            <div className="text-[10px] text-gray-400">
                              {a.stateLicenseCount} states ·{" "}
                              {a.carrierContractCount} carriers ·{" "}
                              {fmtRelativeTime(a.lastSyncedAt)}
                            </div>
                          </div>
                        ) : (
                          <Badge
                            variant="secondary"
                            className="bg-amber-50 text-amber-700 hover:bg-amber-50"
                          >
                            Not registered
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                        {fmtCount(a.quoteCount)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                        {fmtCount(a.totalCases)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">
                        {fmtCount(a.soldApproved)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                        {fmtCurrency(a.appliedFaceTotal)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-blue-700">
                        {fmtCurrency(a.commissionAdvanced)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-rose-600">
                        {a.commissionChargedBack > 0
                          ? `−${fmtCurrency(a.commissionChargedBack)}`
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-emerald-700">
                        {fmtCurrency(a.commissionEarned)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-gray-900">
                        {fmtCurrency(net)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ───────────────────────── Carrier mix ─────────────────────────

function CarrierMixCard({
  carriers,
}: {
  carriers: TechficientCarrierBreakdownRow[];
}) {
  const maxCount = Math.max(1, ...carriers.map((c) => c.caseCount));
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          Carrier mix
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Case volume by carrier across the agency tree.
        </p>

        {carriers.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-8">
            No carrier activity yet.
          </p>
        ) : (
          <div className="space-y-2.5">
            {carriers.slice(0, 12).map((c) => {
              const pct = (c.caseCount / maxCount) * 100;
              const soldPct =
                c.caseCount > 0 ? (c.soldCount / c.caseCount) * 100 : 0;
              return (
                <div key={c.carrierCode || c.carrierName}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-700 font-medium truncate">
                      {c.carrierName}
                    </span>
                    <span className="text-gray-500 tabular-nums">
                      {c.caseCount} ({fmtPercent(soldPct)} sold)
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500/70 rounded-full"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default TechficientCommissionsTab;
